import React, { useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { authStateAtom } from "@/stores/auth.atom";
import { Button } from "@/components/ui/button";
import { NavArrowRight } from "iconoir-react";

// Import các components
import InviteStep from "./components/InviteStep";
import NeighborhoodStep from "./components/NeighborhoodStep";
import CommunityStep from "./components/CommunityStep";
import TrustedContactStep from "./components/TrustedContactStep";
import BirthdayStep from "./components/BirthdayStep";

// Định nghĩa interface cho form data
export interface OnboardingFormData {
  email?: string;
  trustedEmail?: string;
  emergencyEmail?: string;
  birthday?: {
    month: string;
    day: string;
    year: string;
  };
  location?: {
    latitude: number;
    longitude: number;
  };
}

const OnboardingPage: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [formData, setFormData] = useState<OnboardingFormData>({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const authState = useAtomValue(authStateAtom);

  useEffect(() => {
    console.log("Onboarding authState:", authState);
    // Redirect to login if not authenticated
    if (!authState.isAuthenticated) {
      console.log("Not authenticated, redirecting to login");
      navigate("/login");
      return;
    }
  }, [authState, navigate]);

  if (!authState.isAuthenticated) {
    return null;
  }

  // Animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20,
    },
    animate: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: -20,
    },
  };

  // Các bước trong onboarding
  const steps = [
    { name: "Invite", component: InviteStep },
    { name: "Neighborhood", component: NeighborhoodStep },
    { name: "Community", component: CommunityStep },
    { name: "Trusted Contact", component: TrustedContactStep },
    { name: "Birthday", component: BirthdayStep },
  ];

  // Hàm xử lý next step
  const handleNextStep = async () => {
    if (selectedIndex < steps.length - 1) {
      setIsLoading(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsLoading(false);
      setSelectedIndex(selectedIndex + 1);
    } else if (selectedIndex === steps.length - 1) {
      // Nếu đang ở bước cuối cùng, chuyển hướng đến trang feed
      navigate("/feed");
    }
  };

  // Hàm xử lý skip step
  const handleSkipStep = () => {
    if (selectedIndex < steps.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    } else if (selectedIndex === steps.length - 1) {
      // Nếu đang ở bước cuối cùng, chuyển hướng đến trang feed
      navigate("/feed");
    }
  };

  // Hàm update form data
  const updateFormData = (data: Partial<OnboardingFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  // Kiểm tra xem có phải ở bước cuối cùng không
  const isLastStep = selectedIndex === steps.length - 1;

  return (
    <div className="w-96 h-screen bg-pure-white mx-auto relative">
      {/* Header - Skip Button */}
      <div className="w-full p-4 flex justify-end items-center">
        <button onClick={handleSkipStep} className="flex items-center gap-1">
          <div className="text-center text-burnt-orange text-sm font-normal font-sora leading-tight">
            Bỏ qua
          </div>
          <div className="size-5 relative overflow-hidden">
            <NavArrowRight className="h-5 w-5 text-burnt-orange" />
          </div>
        </button>
      </div>

      {/* Content Area */}
      <div className="h-[calc(100vh-140px)] overflow-y-auto modal-scroll-hidden bg-pure-white">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.Panels className="h-full bg-pure-white">
            {steps.map((Step, idx) => (
              <Tab.Panel
                key={idx}
                className="focus:outline-none h-full bg-pure-white"
                static
              >
                <AnimatePresence mode="wait">
                  {idx === selectedIndex && (
                    <motion.div
                      key={idx}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={pageVariants}
                      transition={{ duration: 0.3 }}
                      className="h-full overflow-y-auto modal-scroll-hidden bg-pure-white"
                    >
                      <div className="pb-4 bg-pure-white">
                        <Step.component
                          formData={formData}
                          updateFormData={updateFormData}
                          onNext={handleNextStep}
                          onSkip={handleSkipStep}
                          isLoading={isLoading}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Fixed Bottom Button */}
      <div className="absolute bottom-2.5 left-0 right-0 px-4">
        <Button
          onClick={handleNextStep}
          className={cn(
            "w-full px-4 py-3.5 bg-[#E06448] rounded-full flex justify-center items-center text-[#F4E7DA] text-sm font-medium font-sora leading-none",
            isLastStep
              ? "bg-[#E06448] text-[#F4E7DA] hover:bg-[#d55a3f]"
              : "bg-[#E06448] text-[#F4E7DA] hover:bg-[#d55a3f]",
          )}
        >
          {isLastStep ? "Xác nhận" : "Tiếp tục"}
        </Button>
      </div>
    </div>
  );
};

export default OnboardingPage;
